"use client";

import { useEffect, useRef, useState } from "react";
import { getSelectedFontStrokeWidth, getSelectedFontUrl } from "@/components/scriba/ui/utils/vara-fonts";

interface VaraAnimatedHeaderProps {
  id?: string;
  text?: string;
  className?: string;
  fontSize?: number;
  color?: string;
  duration?: number;
  font?: string;
  textAlign?: "left" | "center" | "right";
  minHeight?: number;
  showUnderline?: boolean;
  onAnimationComplete?: () => void;
  redrawTrigger?: number;
  replaceOnResize?: boolean;
}

export const VaraAnimatedHeader: React.FC<VaraAnimatedHeaderProps> = ({
  id,
  text = "Scriba",
  className = "",
  fontSize = 72,
  color = "currentColor",
  duration = 2000,
  font = getSelectedFontUrl(),
  textAlign = "center",
  minHeight,
  showUnderline = false,
  onAnimationComplete,
  redrawTrigger = 0,
  replaceOnResize = true,
}) => {
  const [isClient, setIsClient] = useState(false);
  const [showUnderlineAnim, setShowUnderlineAnim] = useState(false);
  const [hasResized, setHasResized] = useState(false);
  const containerId = id || `vara-container-${text.toLowerCase().replace(/\s+/g, "-")}`;
  const strokeWidth = getSelectedFontStrokeWidth();
  const initializedRef = useRef(false);

  useEffect(() => {
    setIsClient(true);
    // Load Google Font for fallback on resize
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Satisfy&display=swap";
    link.rel = "stylesheet";
    if (!document.querySelector(`link[href="${link.href}"]`)) {
      document.head.appendChild(link);
    }
  }, []);

  // Detect window resize and switch to text
  useEffect(() => {
    if (!isClient || !replaceOnResize) return;

    const handleResize = () => {
      setHasResized(true);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isClient, replaceOnResize]);

  useEffect(() => {
    if (!isClient) return;

    // Clear container before re-initializing
    const container = document.getElementById(containerId);
    if (container) {
      container.innerHTML = "";
    }

    // Wait for DOM to be ready
    const timer = setTimeout(() => {
      // Dynamically load Vara library
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/vara@1.4.0/lib/vara.min.js";
      script.async = true;

      script.onload = () => {
        const VaraLib = (window as any).Vara;
        const container = document.getElementById(containerId);
        if (VaraLib && container) {
          // Clear any existing content
          container.innerHTML = "";

          const vara = new VaraLib(
            `#${containerId}`,
            font,
            [
              {
                text: text,
                duration: initializedRef.current ? 0 : duration, // Instant draw if already initialized
                textAlign: textAlign,
                color: color,
                fontSize: fontSize,
                strokeWidth: strokeWidth,
              },
            ],
            {
              strokeWidth: strokeWidth,
              color: color,
              fontSize: fontSize,
              autoAnimation: true,
            },
          );

          // After Vara creates the SVG, add extra padding to prevent clipping
          setTimeout(() => {
            const svg = container.querySelector("svg");
            if (svg) {
              const currentHeight = svg.getAttribute("height");
              const _newHeight = currentHeight ? parseFloat(currentHeight) * 1.3 : fontSize * 2;
              svg.style.overflow = "visible";
            }
          }, 50);

          // Handle animation end callbacks
          if (!initializedRef.current) {
            vara.animationEnd(() => {
              if (showUnderline) {
                setShowUnderlineAnim(true);
              }
              if (onAnimationComplete) {
                onAnimationComplete();
              }
            });
          }

          initializedRef.current = true;
        }
      };

      // Only add script if not already in document
      const existingScript = document.querySelector('script[src*="vara.min.js"]');
      if (!existingScript) {
        document.head.appendChild(script);
      } else {
        // Script already loaded, trigger the initialization directly
        const onLoadHandler = script.onload;
        if (onLoadHandler) {
          (onLoadHandler as any)();
        }
      }

      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    }, 0);

    return () => clearTimeout(timer);
  }, [text, fontSize, duration, font, isClient, textAlign, showUnderline, color, strokeWidth, containerId]);

  const justifyClass =
    textAlign === "left" ? "justify-start" : textAlign === "right" ? "justify-end" : "justify-center";
  const widthStyle = textAlign === "center" ? "100%" : "fit-content";
  const heightValue = minHeight ?? fontSize * 2; // Increased from 1.5 to 2 to prevent clipping

  return (
    <div className="relative inline-block transition-all duration-300 ease-in-out h-100" style={{ width: widthStyle }}>
      {/* Vara SVG - fades out on resize */}
      <div
        id={containerId}
        className={`flex items-center ${justifyClass} ${className}`}
        style={{
          minHeight: `${heightValue}px`,
          width: widthStyle,
          maxWidth: textAlign === "center" ? "100%" : "none",
          opacity: hasResized ? 0 : 1,
          animation: hasResized ? "fadeOut 150ms ease-out forwards" : "none",
          position: hasResized ? "absolute" : "relative",
          top: 0,
          left: 0,
        }}
      />
      {/* Regular text - fades in on resize */}
      {hasResized && (
        <div
          className={`flex items-center ${justifyClass} ${className}`}
          style={{
            minHeight: `${heightValue}px`,
            width: widthStyle,
            maxWidth: textAlign === "center" ? "100%" : "none",
            fontFamily: "'Satisfy', cursive",
            fontSize: `${fontSize}px`,
            color: color,
            lineHeight: 1.2,
            opacity: 0,
            animation: "fadeIn 150ms ease-out forwards",
          }}
        >
          {text}
        </div>
      )}
      {showUnderline && showUnderlineAnim && (
        <div
          className="absolute bottom-0 left-0 h-[2px] animate-[underline-draw_0.5s_ease-out_forwards]"
          style={{
            backgroundColor: color,
            width: 0,
            animation: "underline-draw 0.5s ease-out forwards",
          }}
        />
      )}
      <style jsx>{`
        @keyframes underline-draw {
          from {
            width: 0;
          }
          to {
            width: 100%;
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};
